import { forwardRef, useImperativeHandle, useRef, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { currentSelectedLanguage, storeSettingsData, updateUserSettings } from "../../../../../StateManagement/actions";
import { useAdminUserSearch } from "../../hooks/useAdminUserSearch";
import Pagination from "../../../../Controls/Pagination/pagination";
import { useDispatch } from "react-redux";
import APIRequest from "../../../../../Services/commonAPIConsumer";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../../../Controls/Spinner/LoadingSpinner";
import Alert from "../../../../Controls/Alerts/Alerts";

const UserDetails = forwardRef(({ userRoleOptions, getUserDetails }, ref) => {

    const dispatch = useDispatch();
    const userDetails = useSelector(state => state.settings.AdminTab.settingsData);


    const navigate = useNavigate();

    const isUserRoleChanged = useSelector(state => state.settings.AdminTab.settingsData.find(item => item.isDataChanged));
    const [showNoRecords, setNoRecordsFound] = useState(false);
    const selectedLanguage = useSelector(currentSelectedLanguage);

    const [pageView, setPageView] = useState("25");
    const [totalPagesCount, setTotalPagesCount] = useState(1);
    const currentPagevalue = useRef(1);
    const currentPageViewCount = useRef(25);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        isNextDisabled: false,
        isPrevDisabled: true
    });
    const [alertMsg, setAlertMsg] = useState("");
    const [alertType, setAlertType] = useState("");
    const [openLoader, setOpenLoader] = useState(false);

    const userNameRef = useRef();
    const companyNameRef = useRef();
    const roleValueRef = useRef();
    const statusValueRef = useRef();

    const { searchAdminUsers } = useAdminUserSearch();

    const GetAdminSearchUserDetails = async (userName, companyName, roleValue, statusValue) => {
        setOpenLoader(true);
        const { success, data, error } = await searchAdminUsers({
            companyName: companyName,
            userName: userName,
            roleId: roleValue,
            status: statusValue,
            currentPage: currentPagevalue.current,
            pageSize: currentPageViewCount.current
        });

        if (success) {
            const hasNoResults = data.userDetails.length === 0;
            const userRolesData = data.userDetails.map(item => ({
                ...item,
                prevValue: item.roleID,
                isDataChanged: false
            }));
            dispatch(storeSettingsData({
                outerTabSelected: "Admin",
                innerTabSelected: "Users",
                Settings: userRolesData
            }))
            setNoRecordsFound(hasNoResults);
            setTotalPagesCount(data.totalCount);
        } else {
            dispatch(storeSettingsData({
                outerTabSelected: "Admin",
                innerTabSelected: "Users",
                Settings: []
            }))
            setNoRecordsFound(true);
            setTotalPagesCount(1);
            setAlertMsg(error);
            setAlertType('warning');
            // dispatch(showAlert({
            //     alertMsg: error,
            //     alertType: "warning"
            // }))
        }
        setOpenLoader(false);
    };
    // useEffect(() => {
    //     setTimeout(() => {
    //         setAlertMsg("");
    //         setAlertType("");
    //     }, 5000);
    // }, [alertType]);

    useImperativeHandle(ref, () => {
        return {
            getUserDetails(userName, companyName, roleValue, statusValue) {
                userNameRef.current = userName;
                companyNameRef.current = companyName;
                roleValueRef.current = roleValue;
                statusValueRef.current = statusValue;
                GetAdminSearchUserDetails(userName, companyName, roleValue, statusValue);
            }
        }
    });

    useEffect(() => {
        if (getUserDetails.isSearch) {
            userNameRef.current = getUserDetails.userName;
            companyNameRef.current = getUserDetails.companyName;
            roleValueRef.current = getUserDetails.roleValue;
            statusValueRef.current = getUserDetails.statusValue;
            currentPagevalue.current = 1;
            setPagination(prev => ({
                ...prev,
                currentPage: 1,
                isNextDisabled: false,
                isPrevDisabled: true
            }));
            GetAdminSearchUserDetails(getUserDetails.userName, getUserDetails.companyName, getUserDetails.roleValue, getUserDetails.statusValue);
        }
    }, [getUserDetails]);


    const ddlRoleChangeOption = (e, id) => {
        dispatch(updateUserSettings({
            value: e.target.value,
            id: id
        }))
    }

    async function updateUserRoles() {
        try {
            setOpenLoader(true);
            const response = await APIRequest.PUT(
                JSON.stringify(userDetails.filter(userRole => userRole.isDataChanged)),
                `${process.env.REACT_APP_API}${process.env.REACT_APP_API_UpdateUserRoles}`
            );

            if (response === "Server maintenance") {
                navigate("/maintenance");
                return;
            }

            if (response.errorMessage) {
                handleErrorResponse(response.errorMessage);
                return false;
            }

            if (response.status === 200) {
                const updatedUserDetails = userDetails.map(element => ({
                    ...element,
                    ...(element.isDataChanged && {
                        role: element.roleID,
                        prevValue: element.roleID,
                        isDataChanged: false
                    })
                }));


                dispatch(storeSettingsData({
                    outerTabSelected: "Admin",
                    innerTabSelected: "Users",
                    Settings: updatedUserDetails
                }))
                setNoRecordsFound(true);
            }
        } catch (error) {
            handleErrorResponse(error.message || 'An error occurred while updating user roles');
            return false;
        }
        setAlertType("sucess");
        setOpenLoader(false);
    }

    const handleErrorResponse = (errorMessage) => {
        dispatch(storeSettingsData({
            outerTabSelected: "Admin",
            innerTabSelected: "Users",
            Settings: userDetails
        }))
        setNoRecordsFound(true);
        setAlertMsg(
            selectedLanguage.LstLanguageCodes.UI_FailedtoSaveSetting + "."
        );
        setAlertType("warning");
        setOpenLoader(false);
    }



    const paginationAttributes = {
        ...pagination,
        totalPages: totalPagesCount,
    };
    const onPageChange = (pageNumber) => {
        currentPagevalue.current = pageNumber;
        currentPageViewCount.current = pageView;
        setPagination(prev => ({
            ...prev,
            currentPage: pageNumber,
            isNextDisabled: pageNumber === totalPagesCount,
            isPrevDisabled: pageNumber === 1
        }));
        GetAdminSearchUserDetails(userNameRef.current, companyNameRef.current, roleValueRef.current, statusValueRef.current);
    };
    const onPrevClick = () => {
        const newPage = currentPagevalue.current - 1;
        currentPagevalue.current = newPage;
        currentPageViewCount.current = pageView;
        setPagination(prev => ({
            ...prev,
            currentPage: newPage,
            isPrevDisabled: newPage === 1,
            isNextDisabled: false
        }));
        GetAdminSearchUserDetails(userNameRef.current, companyNameRef.current, roleValueRef.current, statusValueRef.current);
    };

    const onNextClick = () => {
        const newPage = currentPagevalue.current + 1;
        currentPagevalue.current = newPage;
        currentPageViewCount.current = pageView;
        setPagination(prev => ({
            ...prev,
            currentPage: newPage,
            isNextDisabled: newPage === totalPagesCount,
            isPrevDisabled: false
        }));
        GetAdminSearchUserDetails(userNameRef.current, companyNameRef.current, roleValueRef.current, statusValueRef.current);
    };
    const setpageViewDrp = (e) => {
        currentPageViewCount.current = e;
        currentPagevalue.current = 1;
        setPageView(e);
        setPagination(prev => ({
            ...prev,
            currentPage: 1,
            isNextDisabled: false,
            isPrevDisabled: true
        }));
        GetAdminSearchUserDetails(userNameRef.current, companyNameRef.current, roleValueRef.current, statusValueRef.current);
    };



    return (
        <>
            <Alert
                alertType={alertType}
                alertMsg={alertMsg}
                setAlertMsg={setAlertMsg}
                setAlertType={setAlertType}
                className="alerthist"
                isfromSeetting="true"></Alert>
            {openLoader ? <LoadingSpinner /> : null}
            {userDetails.length > 0 && (
                <>
                    <div className="comontab-heading-admin">
                        <div className="d-flex align-items-center justify-content-between comonheader-bg-admin">
                            <h6 className=" font-16 font-w600 ps-3 p-2">
                                {
                                    selectedLanguage.LstLanguageCodes
                                        .UI_viewingAdminsettings
                                }
                            </h6>
                            {isUserRoleChanged && (
                                <>
                                    <div className="col-sm-2 admin-save">
                                        <button
                                            type="button"
                                            className="btn sendsubmit px-2rem me-1"
                                            onClick={(e) =>
                                                updateUserRoles(false)
                                            }
                                        >
                                            {
                                                selectedLanguage.LstLanguageCodes
                                                    .UI_Save
                                            }
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}
            {userDetails.length > 0 ? (
                <>
                    <div className="row align-items-center admin-user-list bg-white ">
                        <table
                            className="fold-table table tablelayout "
                            cellPadding="0"
                            cellSpacing="0">
                            <thead className="bg-lightblue">
                                <tr className="">
                                    <th className="w-18p text-start">
                                        {
                                            selectedLanguage.LstLanguageCodes
                                                .UI_Name
                                        }
                                    </th>
                                    <th className="w-20p text-start">
                                        {
                                            selectedLanguage.LstLanguageCodes
                                                .UI_Email
                                        }
                                    </th>
                                    <th className="w-20p text-start">
                                        {
                                            selectedLanguage.LstLanguageCodes
                                                .UI_Company
                                        }
                                    </th>
                                    <th className="w-8p text-start">
                                        {
                                            selectedLanguage.LstLanguageCodes
                                                .UI_Status
                                        }
                                    </th>
                                    <th className="w-10p text-start">
                                        {
                                            selectedLanguage.LstLanguageCodes
                                                .UI_RPortalStatus
                                        }
                                    </th>
                                    <th className="w-20p text-center">
                                        {
                                            selectedLanguage.LstLanguageCodes
                                                .UI_Role
                                        }
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="borderrow">
                                    <td
                                        colSpan={8}
                                        className="innertTableTd">
                                        <table
                                            className={"table-layout"}
                                            cellPadding="0"
                                            cellSpacing="0">
                                            {userDetails.map((userItem) => (
                                                <>
                                                    <tr className="borderrow">
                                                        <td
                                                            className="w-18p text-start pl-12p"
                                                            style={{
                                                                borderLeft:
                                                                    "0.1rem solid rgba(231, 229, 229, 0.44)",
                                                            }}>
                                                            <div className="d-flex flex-wrap textwrap">
                                                                {userItem.userName}
                                                            </div>
                                                        </td>
                                                        <td
                                                            className="w-20p text-start pl-12p"
                                                            style={{
                                                                borderLeft:
                                                                    "0.1rem solid rgba(231, 229, 229, 0.44)",
                                                            }}>
                                                            <div className="d-flex flex-wrap textwrap">
                                                                {userItem.email}
                                                            </div>
                                                        </td>
                                                        <td
                                                            className="w-20p text-start pl-12p"
                                                            style={{
                                                                borderLeft:
                                                                    "0.1rem solid rgba(231, 229, 229, 0.44)",
                                                            }}>
                                                            <div className="d-flex flex-wrap textwrap">
                                                                {userItem.companyName}
                                                            </div>
                                                        </td>
                                                        <td
                                                            className="w-8p text-start pl-12p"
                                                            style={{
                                                                borderLeft:
                                                                    "0.1rem solid rgba(231, 229, 229, 0.44)",
                                                            }}>
                                                            <div className="d-flex flex-wrap textwrap">
                                                                {userItem.isActive}
                                                            </div>
                                                        </td>
                                                        <td
                                                            className="w-10p text-start pl-12p"
                                                            style={{
                                                                borderLeft:
                                                                    "0.1rem solid rgba(231, 229, 229, 0.44)",
                                                            }}>
                                                            <div className="d-flex flex-wrap textwrap">
                                                                {
                                                                    userItem.rPortalUserStatus
                                                                }
                                                            </div>
                                                        </td>
                                                        <td
                                                            className="w-20p text-center"
                                                            style={{
                                                                borderLeft:
                                                                    "0.1rem solid rgba(231, 229, 229, 0.44)",
                                                            }}>
                                                            <select
                                                                value={userItem.roleID}
                                                                className="form-select form-control adminTableTrTd"
                                                                id="ddlRoles"
                                                                onChange={(e) =>
                                                                    ddlRoleChangeOption(
                                                                        e,
                                                                        userItem.id
                                                                    )
                                                                }
                                                                disabled={
                                                                    userItem.roleID ==
                                                                        "65b2243008f7bf9dff3b181a"
                                                                        ? true
                                                                        : userItem.rPortalUserStatus ==
                                                                            "Deleted"
                                                                            ? true
                                                                            : userItem.email !=
                                                                                null &&
                                                                                userItem.email.toLowerCase() ==
                                                                                localStorage
                                                                                    .getItem(
                                                                                        "UserName"
                                                                                    )
                                                                                    .toLowerCase()
                                                                                ? true
                                                                                : false
                                                                }
                                                                aria-label="Default select example">
                                                                {userRoleOptions.map(
                                                                    (option) =>
                                                                        (userItem.roleID ==
                                                                            "65b2243008f7bf9dff3b181a" ||
                                                                            option.id !=
                                                                            "65b2243008f7bf9dff3b181a") && (
                                                                            <option
                                                                                value={option.id}>
                                                                                {
                                                                                    option.roleDescription
                                                                                }
                                                                            </option>
                                                                        )
                                                                )}
                                                            </select>
                                                        </td>
                                                    </tr>
                                                </>
                                            ))}
                                        </table>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <div className="row d-flex justify-content-between">
                            <p className="w-auto">
                                <label
                                    htmlFor="pageView"
                                    className="radio-label font-w600 control res-w  res-left12"
                                    style={{
                                        paddingTop: "9px",
                                        paddingRight: "5px",
                                    }}>
                                    {
                                        selectedLanguage.LstLanguageCodes
                                            .UI_pageView
                                    }
                                    :
                                </label>
                                <select
                                    className=""
                                    name="documentIdInput"
                                    value={pageView}
                                    onChange={(e) =>
                                        setpageViewDrp(e.target.value)
                                    }
                                >
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </p>
                            {totalPagesCount > 1 ? (
                                <Pagination
                                    {...paginationAttributes}
                                    onPrevClick={onPrevClick}
                                    onNextClick={onNextClick}
                                    onPageChange={onPageChange}
                                />
                            ) : (
                                ""
                            )}
                        </div>
                    </div>
                </>
            ) : (
                <div className="row align-items-center admin-user-list mtop-admin">
                    {showNoRecords && (
                        <div className="row justify-content-center">
                            {
                                selectedLanguage.LstLanguageCodes
                                    .UI_NoRecordsFound
                            }
                        </div>
                    )}
                </div>
            )}
        </>
    );
});

export default UserDetails;